// catalog.json shape (SPEC "Document model"): books and chapters with slugs,
// titles, and source language. Original ↔ retelling pairing is by chapter
// number — chapter N of the original pairs with chapter N of the retelling.
// Zod schema validates the JSON on fetch; TS types are inferred from it.

// Namespace import, not `import { z }` — zod ships CJS for the node
// condition and the named form resolves to `undefined` under vitest's
// multi-project run. Same fix as packages/logging/src/ingest.ts; it is
// zod 4's documented import form anyway.
import * as z from "zod";

export const catalogChapterSchema = z
  .object({
    /** Pairing key: original chapter N ↔ retelling chapter N. */
    number: z.number().int().positive(),
    /**
     * Always the chapter's real prose title (e.g. "The Secret of the Bed"),
     * free or premium alike. The paywall's disclosure boundary is
     * title-vs-text, not free-vs-premium — a table of contents is marketing,
     * not content — so a premium chapter's title ships in the public corpus
     * exactly like a free one's; only its `retelling`/`anchors` pointers
     * below are withheld. See `premium.getChapter` (Convex) for the gated
     * text side.
     */
    title: z.string(),
    /**
     * Corpus file pointers, relative to `public/corpus/`, under content-hashed
     * names so a redeploy busts CDN/browser caches cleanly (SPEC pipeline §3).
     * The reader fetches these instead of guessing filenames.
     *
     * The ORIGINAL always ships publicly (public-domain source). The retelling
     * + anchors are ABSENT for a premium (paywalled) chapter — its retelling
     * text is served only through the entitlement-gated Convex query
     * (`premium.getChapter`), never emitted into the static corpus (plan 10
     * ADR-1). Hence both are optional and the refinement below ties their
     * presence to `premium`.
     */
    original: z.string(),
    retelling: z.string().optional(),
    anchors: z.string().optional(),
    /**
     * Locked (paywalled) chapter. Free chapters carry retelling + anchors and
     * OMIT this key; premium chapters set it `true` and carry neither pointer.
     *
     * Optional (not `.default(false)`) on purpose: a default would materialize
     * `premium: false` on every free row at parse time, changing the emitted
     * `catalog.json` bytes for chapters the paywall must leave untouched. With
     * `.optional()` the build writes this key only when `true`, so free rows
     * are byte-identical to a pre-paywall build. Read absence as free
     * (`chapter.premium === true` is the premium test).
     */
    premium: z.boolean().optional(),
    /**
     * The product id that unlocks THIS chapter — set alongside `premium` for a
     * locked row, absent for a free one. ADDITIVE ONLY, deliberately not a
     * repurposing of `premium`: `catalog.json` is fetched from a fixed,
     * non-content-hashed URL (see `fromChapter`'s comment above for why that
     * matters), so a stale cached client that predates this field simply never
     * reads it and keeps working off `premium` alone, exactly as before. Lets a
     * chapter resolve its OWN paywall product (a volume's, once a book is
     * priced per volume) rather than the book-level `CatalogBook.premium.productId`,
     * which names the whole-title bundle, not any one chapter's unlock.
     */
    premiumProductId: z.string().optional(),
  })
  .refine(
    (c) =>
      c.premium
        ? c.retelling === undefined && c.anchors === undefined
        : c.retelling !== undefined && c.anchors !== undefined,
    {
      message:
        "a free chapter must carry both retelling and anchors; a premium " +
        "chapter must carry neither (its text is gated behind premium.getChapter)",
    },
  );
export type CatalogChapter = z.infer<typeof catalogChapterSchema>;

/**
 * The canonical (current) placement form. Everything downstream sees only this
 * tagged union — see {@link matterPlacementSchema}, which is what the catalog is
 * actually parsed with.
 */
const matterPlacementObjectSchema = z.union([
  z.object({ at: z.literal("front") }),
  z.object({ at: z.literal("back") }),
  z.object({ at: z.literal("before"), chapter: z.number().int().positive() }),
  z.object({ at: z.literal("after"), chapter: z.number().int().positive() }),
]);

/**
 * Where a matter document sits in reading order — the catalog mirror of
 * `MatterPlacement` in `convex/model/bookConfig.ts`, which documents why the
 * anchor names a chapter rather than a volume. `front`/`back` are the
 * whole-book positions; `before`/`after` anchor to a named chapter, which is
 * what lets a multi-volume book put an afterword mid-catalog.
 *
 * The anchored variants carry a chapter number that this book need not
 * actually have: the Odyssey's volume-three matter is configured against
 * chapters 13–24, which stay out of the catalog until they're seeded. That is
 * a reading-order question, not a schema one — see `readingOrder`, which drops
 * matter whose anchor chapter is absent rather than guessing a position for
 * it.
 *
 * COMPATIBILITY SHIM — the `preprocess` step. Before the anchored variants
 * existed, `placement` was the bare string `"front"` or `"back"`; it is now
 * always an object, and a bare string is normalized to the equivalent
 * whole-book position instead of being rejected. Without that step the widening
 * would be a whole-catalog outage rather than a matter bug: unlike every other
 * corpus artifact, `catalog.json` is fetched by a FIXED, NON-content-hashed URL
 * (see `CATALOG_URL` in `corpus.ts`), and `corpusCatalogSchema.parse` validates
 * the entire payload in one call. So a current client handed a stale CDN- or
 * browser-cached catalog would fail to parse it at all, and EVERY book —
 * chapters included — would stop rendering, on a page that never mentions
 * matter.
 *
 * Normalizing in `preprocess` rather than as an extra union member keeps the
 * parsed type the tagged union alone, so `shelf.ts`'s exhaustive
 * `switch (placement.at)` never has to learn about the legacy spelling.
 *
 * The shim only absorbs skew in this direction. An OLD client (a bundle already
 * in a browser, whose schema predates the widening) handed a NEW catalog still
 * fails, and nothing here can help it — that client's code is already shipped.
 * Removable once no pre-widening `catalog.json` can still be in a cache: after
 * the CDN/browser cache lifetime of that fixed URL has elapsed past the deploy
 * that shipped object placements. Dropping it earlier only re-opens the skew
 * window; nothing else depends on it.
 */
export const matterPlacementSchema = z.preprocess(
  (value) => (value === "front" || value === "back" ? { at: value } : value),
  matterPlacementObjectSchema,
);
export type MatterPlacement = z.infer<typeof matterPlacementSchema>;

/**
 * One front/back matter document (foreword, afterword, about-the-author —
 * PD-2). Matter has no original counterpart: no pairing number, no anchors
 * file, no translation popover — a single `DocumentModel` rendered as one
 * column. `slug` doubles as the `/read/:book/:slug` route segment and is
 * guaranteed non-numeric (chapter routes are all-digits).
 */
export const catalogMatterSchema = z
  .object({
    // Non-numeric, matching build-corpus's all-digits check — the reader route
    // tells a matter slug from a chapter number this way, so an all-digits slug
    // from untrusted catalog data would produce an unroutable entry.
    slug: z
      .string()
      .min(1)
      .refine((s) => !/^\d+$/.test(s), "matter slug must not be all digits"),
    /**
     * Display title. Extracted from the matter file's `#` heading at seed time
     * and stored on `matterMaster` thereafter — the same title the workbench
     * edits, not one re-derived from the source repo on every build.
     */
    title: z.string(),
    /** Reading-order placement — see {@link matterPlacementSchema}. */
    placement: matterPlacementSchema,
    /**
     * Content-hashed `DocumentModel` filename, like `catalogChapter.retelling`.
     * ABSENT for a paywalled document, whose text is never emitted into the
     * public corpus — it is served by `premium.getMatter` instead.
     */
    document: z.string().optional(),
    /**
     * Is this document paywalled? Matter is free by DEFAULT and paywalled by
     * opt-in, so the key is omitted on a free document exactly as it is on a
     * free chapter — a catalog.json built before this field existed parses
     * unchanged, with every document free, which is what it was.
     */
    premium: z.boolean().optional(),
    /** The product that unlocks it — set alongside `premium`, derived from the
     *  document's placement at build time (`productIdForMatter`). */
    premiumProductId: z.string().optional(),
  })
  .refine(
    (m) => (m.premium ? m.document === undefined : m.document !== undefined),
    {
      message:
        "a free matter document must carry its document; a paywalled one must " +
        "not (its text is gated behind premium.getMatter)",
    },
  );
export type CatalogMatter = z.infer<typeof catalogMatterSchema>;

export const catalogBookSchema = z.object({
  slug: z.string(),
  title: z.string(),
  /**
   * Language of the original, lowercase code (e.g. "en", "grc") — the source
   * repo's `source.language` (book.yaml), surfaced under this name. "en"
   * disables the original-pane translation popover for the whole book.
   */
  sourceLanguage: z.string(),
  chapters: z.array(catalogChapterSchema),
  /**
   * Front/back matter in reading order within each placement (PD-2). Defaulted
   * so a catalog.json from a pre-matter build still validates.
   */
  matter: z.array(catalogMatterSchema).default([]),
  /**
   * The book's paywalled tier, when it has one (plan 10). The upsell reads
   * `productId` to fetch the live price/name via `checkout.getProduct`. No
   * price lives in the catalog — it comes live from `getProduct` so the
   * displayed and attested amounts can never drift. Absent → the whole book is
   * free.
   *
   * Which chapters are locked is carried by the chapter rows themselves
   * (`premium: true`), not here: since the free set went non-contiguous — a
   * multi-volume book opens each later volume free — a single `fromChapter`
   * can no longer describe the boundary truthfully, so nothing reads it.
   */
  premium: z
    .object({
      productId: z.string(),
      /**
       * LEGACY, write-only, removable one deploy after the paywall rollout.
       * Read by nothing here — it exists so a client still running the PREVIOUS
       * bundle can parse the current `catalog.json`, whose schema required this
       * key. `catalog.json` is the one artifact fetched from a fixed,
       * non-content-hashed URL (`CATALOG_URL`), so an old tab that first fetches
       * it after a deploy gets the new file; without this key its
       * `corpusCatalogSchema.parse` throws on the first book carrying `premium`,
       * and since every book now carries one that is a blank library rather
       * than a paywall bug. Declared (not merely emitted) because `parse`
       * strips unknown keys — an undeclared key would never reach the file.
       */
      fromChapter: z.number().int().positive().optional(),
    })
    .optional(),
});
export type CatalogBook = z.infer<typeof catalogBookSchema>;

export const corpusCatalogSchema = z.object({
  books: z.array(catalogBookSchema),
});
export type CorpusCatalog = z.infer<typeof corpusCatalogSchema>;
